import { useCallback, useState } from 'react';
import { Box } from '@mui/material';
import { extractErrorMessage } from 'app/providers/with-wagmi-auth/utils';
import { packagesModel } from 'entities/mine-package';
import { paymentModel } from 'entities/payment';
import { PaymentType, selectEstimatedPayment, selectPaymentAssets, TokenId } from 'entities/payment/model';
import { ConfirmPaymentButtonWithReferralCode } from 'features/payment-actions';
import { Tap2MineReferralInput } from 'features/tap2mine-referral';
import { getToastMessage, useAppDispatch, useShallowSelector, useStepper } from 'shared';

import { STEPPER_ROUTES } from '../../../lib/stepper';
import { BackgroundCard, Debit, DebitWarning } from '../../shared';

import { DebitTokenSelector } from './debit-token-selector';

export const PaymentConfirmationDebitView = () => {
  const { goBack, next } = useStepper();
  const dispatch = useAppDispatch();
  const [selectedToken, setSelectedToken] = useState<TokenId | undefined>(undefined);

  const currentPackage = useShallowSelector(packagesModel.selectCurrentPackage);
  const { tokens, networks } = useShallowSelector(selectPaymentAssets);
  const { amountToPay: options } = useShallowSelector(selectEstimatedPayment);

  const handleConfirm = useCallback(
    async (refCode?: string) => {
      if (!selectedToken) return;

      const currentToken = tokens.byId[selectedToken];
      dispatch(paymentModel.actions.setPaymentTokenId(selectedToken));

      await dispatch(
        paymentModel.thunks.confirmPayment({
          network: networks.byId[currentToken.networkId].name,
          packageName: currentPackage.name,
          paymentTokenSymbol: currentToken.symbol,
          paymentType: PaymentType.debit,
          referralCode: refCode,
        }),
      ).unwrap();
    },
    [currentPackage.name, dispatch, networks.byId, selectedToken, tokens.byId],
  );

  const handleBackNavigation = () => {
    dispatch(paymentModel.actions.resetPaymentTokenId());
    goBack();
  };

  return (
    <BackgroundCard title="Payment Confirmation" onBack={handleBackNavigation}>
      <Box component="img" alt="Debit" src={Debit} sx={{ width: 66, height: 50, objectFit: 'contain' }} mb={2} />
      <DebitTokenSelector
        networks={networks}
        options={options}
        tokens={tokens}
        selected={selectedToken}
        onSelect={(v) => setSelectedToken(v)}
        packageName={currentPackage.name}
      />

      <DebitWarning>
        Make sure that the currency selected in the Onramper matches the currency you selected on this screen.
        Otherwise, it may lead to loss of funds.
      </DebitWarning>

      <Tap2MineReferralInput />

      <ConfirmPaymentButtonWithReferralCode
        disabled={!selectedToken}
        confirmAction={handleConfirm}
        onSuccessConfirm={() => next(STEPPER_ROUTES.PAYMENT_DEBIT_AWAITING)}
      />
    </BackgroundCard>
  );
};
